<script setup>
    import Store from '../store';
    import Button from 'primevue/button';
    import InputNumber from 'primevue/inputnumber';
    import Checkbox from 'primevue/checkbox';
    import { FilterMatchMode, FilterOperator } from 'primevue/api';
    import DataTable from 'primevue/datatable';
    import InputText from 'primevue/inputtext';
    import Column from 'primevue/column';
    import Dialog from 'primevue/dialog';
    import InputSwitch from 'primevue/inputswitch';
    import { ref, onBeforeMount, onMounted } from 'vue';
    import api from '../lib/serverApi';
    import utils from '../lib/utils';
    import dialogs from '../lib/dialogs';
    import HistoryData from '../components/HistoryData.vue';

    const entitieValues = ref(null);
    const selectionEntitie = ref(null);
    const filters = ref(null);
    const loading = ref(false);
    const showID = ref(true);
    const historyDialog = ref({
        visible: false,
        header: '',
        entitieId: ''
    });

    const editValueDialog = ref({
        visible: false,
        header: '',
        entitieId: '',
        valueType: '',
        value: '',
        workOnTime: [],
        pulseTime: 0
    });

    const hisoryDialogMaximized = ref(false);

    onBeforeMount(() => {
        Store.commit('SET_FORM_HEADER', 'Администрирование > Значения');
        initFilters();
    });

    onMounted(() => {
        loadEntitieValues();
    });

    async function loadEntitieValues() {
        loading.value = true;
        try {
            entitieValues.value = await api.getEntitieValues(['*'], true);
        } catch(error) {
            dialogs.showException(error);
        }
        loading.value = false;
    }

    const initFilters = () => {
        filters.value = {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            entitieId: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            description: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
        };
    }

    function momentString(moment) {
        if (moment && moment > 2) return utils.dateToString(moment);
        else return '------';
    }

    async function editValue(entitie) {
        try {
            editValueDialog.value.valueType = (await api.getEntitiesConfig([entitie.entitieId]))[0].type;
        } catch (error) {
            dialogs.showError(error.message);
            return;
        }

        editValueDialog.value.visible = true;
        editValueDialog.value.header = 'Редактирование - ' + entitie.description;
        editValueDialog.value.entitieId = entitie.entitieId;
        editValueDialog.value.value = entitie.value;
    }

    async function showGraph(entitie) {
        historyDialog.value.visible = true;
        historyDialog.value.header = 'Архивные данные - ' + entitie.description;
        historyDialog.value.entitieId = entitie.entitieId;
    }

    async function onSaveEditValue() {
        try {
            if (editValueDialog.value.valueType === 'boolean' && editValueDialog.value.workOnTime.length > 0) {

                await api.setEntitieValue(editValueDialog.value.entitieId, {
                    value: editValueDialog.value.value ? editValueDialog.value.pulseTime * 1000 : editValueDialog.value.pulseTime * (-1000),
                    moment: new Date(),
                    status: 'COMMANDPULSE'
                });
            } else
                await api.setEntitieValue(editValueDialog.value.entitieId, {
                    value: editValueDialog.value.value,
                    moment: new Date(),
                    status: 'COMMAND'
                });
            loadEntitieValues();
        } catch(error) {
            dialogs.showException(error);
        }

        editValueDialog.value.visible = false;
    }
</script>

<template>
    <div class="formPanel">
        <div class="mb-2">
            <Button icon="pi pi-sync" label="Обновить" @click="loadEntitieValues()" class="mr-1" />
        </div>
        <DataTable
            :value="entitieValues"
            :paginator="true"
            stripedRows
            v-model:selection="selectionEntitie"
            selectionMode="single"
            class="p-datatable-sm"
            :rows="20"
            :rowsPerPageOptions="[10, 15, 20, 50, 100]"
            dataKey="entitieId"
            :rowHover="true"
            v-model:filters="filters"
            filterDisplay="menu"
            :loading="loading"
            :filters="filters"
            responsiveLayout="scroll"
            :globalFilterFields="['entitieId', 'description']"
        >
            <template #header>
                <div class="flex justify-content-between flex-column sm:flex-row">
                    <Button type="button" icon="pi pi-filter-slash" label="Очистить фильтр" class="p-button-outlined" @click="initFilters()" />
                    <div class="flex align-items-center">
                        Показывать идентификатор
                        <InputSwitch class="ml-3" v-model="showID" />
                    </div>
                    <span class="p-input-icon-left" style="width: 300px">
                        <i class="pi pi-search" />
                        <InputText v-model="filters['global'].value" placeholder="текст поиска" style="width: 100%" />
                    </span>
                </div>
            </template>
            <template #empty>Нет данны для отображения.</template>
            <template #loading> Загрузка данных. Пожалуйста подождите. </template>
            <Column v-if="showID" field="entitieId" header="Идентификатор" style="min-width: 12rem">
                <template #body="{ data }">
                    {{ data.entitieId }}
                </template>
                <template #filter="{ filterModel }">
                    <InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Поиск по идентификатору" />
                </template>
            </Column>
            <Column field="description" header="Описание" style="min-width: 12rem">
                <template #body="{ data }">
                    {{ data.description }}
                </template>
                <template #filter="{ filterModel }">
                    <InputText type="text" v-model="filterModel.value" class="p-column-filter" placeholder="Поиск по описанию" />
                </template>
            </Column>
            <Column field="value" header="Значение" style="min-width: 8rem">
                <template #body="{ data }">
                    {{ data.value }}
                </template>
            </Column>
            <Column field="status" header="Статус" style="min-width: 5rem">
                <template #body="{ data }">
                    {{ data.status }}
                </template>
            </Column>
            <Column field="moment" header="Момент" style="min-width: 8rem">
                <template #body="{ data }">
                    {{ momentString(data.moment) }}
                </template>
            </Column>
            <Column :exportable="false" header="Действия" style="min-width:4rem">
                <template #body="{ data }">
                    <i class="pi pi-pencil mr-3" style="font-size: 1rem" @click="editValue(data)" />
                    <i class="pi pi-chart-line" style="font-size: 1rem" @click="showGraph(data)" />
                </template>
            </Column>
        </DataTable>
    </div>

    <Dialog v-model:visible="historyDialog.visible"
        modal
        maximizable
        :header="historyDialog.header"
        :style="{ width: '80rem' }"
        @maximize="hisoryDialogMaximized = true"
        @unmaximize="hisoryDialogMaximized = false"
    >
        <div class="h-full">
            <HistoryData
                :entitieId="historyDialog.entitieId"
                :maximized="hisoryDialogMaximized"
            />
        </div>
    </Dialog>

    <Dialog v-model:visible="editValueDialog.visible" modal :header="editValueDialog.header" :style="{ width: '50rem' }">

        <div class="p-fluid pt-5 pb-2 px-4">
            <div class="field grid" v-if="editValueDialog.valueType === 'number'">
                <label class="col-12 mb-0 md:col-3 md:mb-0 text-lg font-bold">Значение</label>
                <div class="col-12 md:col-9">
                    <InputNumber v-model="editValueDialog.value" />
                </div>
            </div>

            <div v-if="editValueDialog.valueType === 'boolean'">
                <div class="grid">
                    <div class="col-6">
                        <label class="text-lg font-bold" >Значение</label>
                    </div>
                    <div class="col-6">
                        <InputSwitch v-model="editValueDialog.value" />
                    </div>
                </div>
                <div class="grid">
                    <div class="col-6 flex align-items-center gap-2">
                        <Checkbox v-model="editValueDialog.workOnTime" inputId="workontime" value="workOnTime" />
                        <label for="workontime">Включить на время</label>
                    </div>
                    <div class="col-6 flex align-items-center gap-2">
                        <input class="surface-border h-2rem w-8rem" type="number" v-model="editValueDialog.pulseTime" min="1" max="86400">
                        <label>сек</label>
                    </div>
                </div>
            </div>

        </div>

        <template #footer>
            <Button label="Ok" icon="pi pi-check" @click="onSaveEditValue" />
            <Button label="Отмена" icon="pi pi-check" @click="editValueDialog.visible = false" autofocus severity="warning" />
        </template>
    </Dialog>
</template>

<style scoped>
</style>